import { useQueryClient } from '@tanstack/vue-query'
import { useAuthStore } from '~/stores/auth'
import { useUserStore } from '~/stores/user'
import { accessMeQueryOptions } from '~/queries/v2/access/useAccessMeQuery'
import {
  permissionScopeFor,
  resolvePermissionAction,
} from '~/utils/permissionGate'
import { tryCatch } from '~/utils/tryCatch'

/**
 * Page rule: a page that declares `definePageMeta({ permission })` opens only
 * for someone whose rules from `GET /v2/access/me` grant it, in the page's
 * organisation (and workspace, for a workspace permission). Anyone else goes
 * to /403, like the membership gate.
 *
 * Runs after `05.access`, so the person is already a member of the
 * organisation or workspace in the URL and its id resolves from /users/me.
 * `06.user-role` lets organisation settings pages that declare a permission
 * through to here, and still refuses non-Superadmins on any that declare
 * none. The flow itself is `resolvePermissionAction` (~/utils/permissionGate.ts).
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const permission = to.meta.permission
  if (!permission) return

  const authStore = useAuthStore()

  // Not signed in → auth middleware owns the redirect.
  if (!authStore.isAuthenticated) return

  // Resolve the client before the first await, while the app context is
  // still current.
  const queryClient = useQueryClient()
  const userStore = useUserStore()

  try {
    await userStore.$ensureInitialised()
  } catch {
    // Fall through: an unresolved organisation answers no below.
  }

  const options = accessMeQueryOptions()
  const decision = await resolvePermissionAction({
    permission,
    scope: permissionScopeFor(to.params, userStore.organizations),
    loadRules: async () =>
      (await tryCatch(queryClient.ensureQueryData(options))).data?.rules,
    refreshRules: async () =>
      (await tryCatch(queryClient.fetchQuery({ ...options, staleTime: 0 })))
        .data?.rules,
  })

  if (decision.type === 'redirect') return navigateTo(decision.path)
})
